import type { ColorId } from '@color-dots/game';

const RIM_IDS: ColorId[] = ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'];

/** Public asset paths (§13 v2 — ornate rim sprites). */
export const GEAR_RIM_SPRITE_URLS: Record<ColorId, string> = {
  c1: '/assets/gear/rims/c1.svg',
  c2: '/assets/gear/rims/c2.svg',
  c3: '/assets/gear/rims/c3.svg',
  c4: '/assets/gear/rims/c4.svg',
  c5: '/assets/gear/rims/c5.svg',
  c6: '/assets/gear/rims/c6.svg',
};

const cache = new Map<ColorId, HTMLImageElement>();
let preloadPromise: Promise<void> | null = null;

export function isGearRimColorId(colorId: string): colorId is ColorId {
  return RIM_IDS.includes(colorId as ColorId);
}

export function isGearRimSpriteReady(colorId: string): boolean {
  return isGearRimColorId(colorId) && cache.has(colorId) && cache.get(colorId)!.complete;
}

export function getGearRimSpriteLoadCount(): number {
  return cache.size;
}

/** Preload SVG rim sprites; procedural fallback until images resolve. */
export function preloadGearRimSprites(): Promise<void> {
  if (preloadPromise) return preloadPromise;
  if (typeof Image === 'undefined') {
    preloadPromise = Promise.resolve();
    return preloadPromise;
  }

  preloadPromise = Promise.all(
    RIM_IDS.map(
      (id) =>
        new Promise<void>((resolve) => {
          if (cache.has(id)) {
            resolve();
            return;
          }
          const img = new Image();
          img.onload = () => {
            cache.set(id, img);
            resolve();
          };
          img.onerror = () => resolve();
          img.src = GEAR_RIM_SPRITE_URLS[id];
        }),
    ),
  ).then(() => undefined);

  return preloadPromise;
}

/** Test hook: inject a loaded image without network. */
export function setGearRimSpriteForTest(colorId: ColorId, image: HTMLImageElement): void {
  cache.set(colorId, image);
}

export function clearGearRimSpritesForTest(): void {
  cache.clear();
  preloadPromise = null;
}

/**
 * Draw ornate rim sprite centered at (x,y). Returns true when sprite was drawn.
 * Size: diameter ≈ 2 × radius × 0.82 (matches procedural rimR).
 */
export function drawGearRimSprite(
  ctx: CanvasRenderingContext2D,
  colorId: string,
  x: number,
  y: number,
  radius: number,
): boolean {
  if (!isGearRimColorId(colorId)) return false;
  const img = cache.get(colorId);
  if (!img?.complete || img.naturalWidth === 0) return false;

  const size = radius * 1.64;
  ctx.drawImage(img, x - size / 2, y - size / 2, size, size);
  return true;
}
